import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // Apply the correct layout on initial load
    requestIdleCallback(() => this.#initLayout());
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Initialises the grid layout on page load.
   * Restores sessionStorage preference for returning visitors,
   * or reads the checked radio for first-time visitors.
   */
  #initLayout = () => {
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const savedView = sessionStorage.getItem(`product-grid-view-${viewport}`);

    if (savedView) {
      // Returning visitor — restore their saved preference
      this.#setLayout(savedView);

      // Sync the radio button to match
      const input = this.querySelector(
        `input[type="radio"][value="${savedView}"]`
      );
      if (input instanceof HTMLInputElement) input.checked = true;
    } else {
      // First visit — read whichever radio is checked in the HTML
      const radioName = mediaQueryLarge.matches ? 'grid' : 'grid-mobile';
      const checkedInput = this.querySelector(
        `input[type="radio"][name="${radioName}"]:checked`
      );
      if (checkedInput instanceof HTMLInputElement) {
        this.#setLayout(checkedInput.value);
      }
    }
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event (e.g. rotating device,
   * or resizing browser across the mobile/desktop breakpoint).
   * Restores sessionStorage preference for the new viewport,
   * or falls back to the checked radio default.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const viewport = event.matches ? 'desktop' : 'mobile';
    const savedView = sessionStorage.getItem(`product-grid-view-${viewport}`);

    if (savedView) {
      // Restore saved preference for this viewport
      this.#setLayout(savedView);

      const input = this.querySelector(
        `input[type="radio"][value="${savedView}"]`
      );
      if (input instanceof HTMLInputElement) input.checked = true;
    } else {
      // No saved preference — fall back to the checked radio default
      const radioName = event.matches ? 'grid' : 'grid-mobile';
      const targetElement = this.querySelector(
        `input[type="radio"][name="${radioName}"]:checked`
      );

      if (!(targetElement instanceof HTMLInputElement)) return;

      targetElement.checked = true;
      this.#setLayout(targetElement.value);
    }
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}